'use client'

import { ScheduledTask } from '@/lib/features/scheduled-tasks/scheduledTasksApi'
import { getToolDisplay } from '@/utils/copilotToolMappings'
import { Bell, Calendar, Clock, MoreVertical, Pause, Play, Star, Trash2, X } from 'lucide-react'
import { useState } from 'react'

interface ScheduledTaskCardProps {
  task: ScheduledTask
  onClick: () => void
  onDelete: () => void
  onFreeze: () => void
  onUnfreeze: () => void
  onSkipNext: () => void
  onToggleFavourite: () => void
  onToggleEmailNotification: () => void
  formatRelativeTime: (date: Date | string | null | undefined) => string
}

const statusColors = {
  PENDING: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  RUNNING: 'bg-blue-100 text-blue-800 border-blue-200',
  COMPLETED: 'bg-green-100 text-green-800 border-green-200',
  FAILED: 'bg-red-100 text-red-800 border-red-200',
  CANCELLED: 'bg-gray-100 text-gray-800 border-gray-200',
  PAUSED: 'bg-orange-100 text-orange-800 border-orange-200',
}

const statusIcons = {
  PENDING: Clock,
  RUNNING: Play,
  COMPLETED: Calendar,
  FAILED: X,
  CANCELLED: X,
  PAUSED: Pause,
}

export function ScheduledTaskCard({
  task,
  onClick,
  onDelete,
  onFreeze,
  onUnfreeze,
  onSkipNext,
  onToggleFavourite,
  onToggleEmailNotification,
  formatRelativeTime,
}: ScheduledTaskCardProps) {
  const [showMenu, setShowMenu] = useState(false)
  const StatusIcon = statusIcons[task.status]

  return (
    <div
      className="group relative cursor-pointer overflow-hidden rounded-3xl border border-gray-200/50 bg-white/70 p-6 text-black shadow-xl backdrop-blur-[8px] transition-all hover:shadow-2xl"
      onClick={onClick}
    >
      {task.unseenResultCount > 0 && (
        <span className="absolute -right-1.5 -top-1.5 h-5 w-5 rounded-full border-2 border-white bg-blue-500" />
      )}
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <div className="mb-2 flex items-center gap-2">
            <h3 className="min-w-0 truncate text-lg font-semibold text-gray-900">{task.name}</h3>
            <span
              className={`inline-flex flex-shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${statusColors[task.status]}`}
            >
              <StatusIcon size={12} />
              {task.status === 'PENDING'
                ? 'Scheduled'
                : task.status === 'PAUSED'
                  ? 'Frozen'
                  : task.status === 'CANCELLED'
                    ? 'Skipped'
                    : task.status}
            </span>
          </div>

          {task.description && (
            <p className="mb-4 line-clamp-2 text-sm text-gray-600">{task.description}</p>
          )}

          <div className="space-y-2">
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <Clock size={14} />
              <span>
                {task.nextRunAt
                  ? `Next run: ${formatRelativeTime(task.nextRunAt)}`
                  : 'No next run scheduled'}
              </span>
            </div>

            {task.lastRunAt && (
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <Calendar size={14} />
                <span>Last run: {formatRelativeTime(task.lastRunAt)}</span>
              </div>
            )}

            <div className="flex items-center gap-4 text-xs text-gray-500">
              <span>
                {task.runCount} {task.runCount === 1 ? 'run' : 'runs'}
                {task.maxRuns && ` of ${task.maxRuns}`}
              </span>
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              {task.toolCalls.map((tc, i) => {
                const { displayName, icon } = getToolDisplay(tc.function.name)
                return (
                  <span
                    key={i}
                    className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-xs text-gray-600"
                  >
                    <span className="flex-shrink-0 [&>svg]:h-3 [&>svg]:w-3">{icon}</span>
                    <span className="truncate">{displayName}</span>
                  </span>
                )
              })}
            </div>
          </div>
        </div>

        <div className="ml-4 flex items-center gap-1">
          <button
            onClick={(e) => {
              e.stopPropagation()
              onToggleFavourite()
            }}
            className="rounded-full p-1.5 text-gray-400 hover:text-yellow-500"
            title={task.bookmark ? 'Remove from favourites' : 'Add to favourites'}
            aria-label={task.bookmark ? 'Remove from favourites' : 'Add to favourites'}
          >
            <Star
              size={16}
              className={task.bookmark ? 'fill-yellow-400 text-yellow-400' : ''}
              aria-hidden
            />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation()
              onToggleEmailNotification()
            }}
            className="rounded-full p-1.5 text-gray-400 hover:text-blue-500"
            title={
              task.notificationSettings?.email?.enabled
                ? 'Disable email notifications'
                : 'Enable email notifications'
            }
            aria-label={
              task.notificationSettings?.email?.enabled
                ? 'Disable email notifications'
                : 'Enable email notifications'
            }
          >
            <Bell
              size={16}
              className={
                task.notificationSettings?.email?.enabled ? 'fill-blue-500 text-blue-500' : ''
              }
              aria-hidden
            />
          </button>
          <div className="relative">
            <button
              onClick={(e) => {
                e.stopPropagation()
                setShowMenu(!showMenu)
              }}
              className="rounded-full p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            >
              <MoreVertical size={16} />
            </button>

            {showMenu && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={(e) => {
                    e.stopPropagation()
                    setShowMenu(false)
                  }}
                />
                <div className="absolute right-0 top-8 z-20 w-48 rounded-2xl border border-gray-200 bg-white shadow-lg">
                  {task.status === 'PENDING' || task.status === 'RUNNING' ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        onFreeze()
                        setShowMenu(false)
                      }}
                      className="flex w-full items-center gap-2 rounded-t-2xl px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <Pause size={14} />
                      Freeze
                    </button>
                  ) : task.status === 'PAUSED' ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        onUnfreeze()
                        setShowMenu(false)
                      }}
                      className="flex w-full items-center gap-2 rounded-t-2xl px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <Play size={14} />
                      Unfreeze
                    </button>
                  ) : null}

                  {task.status !== 'CANCELLED' && task.status !== 'COMPLETED' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        onSkipNext()
                        setShowMenu(false)
                      }}
                      className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <X size={14} />
                      Skip next
                    </button>
                  )}

                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      if (confirm('Delete this scheduled task? This cannot be undone.')) {
                        onDelete()
                      }
                      setShowMenu(false)
                    }}
                    className="flex w-full items-center gap-2 rounded-b-2xl px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50"
                  >
                    <Trash2 size={14} />
                    Delete
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
